import { useState } from 'react';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { WalletHeader } from '@/components/wallet/WalletHeader';
import { TransactionList } from '@/components/wallet/TransactionList';
import { DepositModal } from '@/components/wallet/DepositModal';
import { WithdrawModal } from '@/components/wallet/WithdrawModal';
import { LinkAccountsModal } from '@/components/wallet/LinkAccountsModal';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useWallet } from '@/hooks/useWallet';
import { useTransactions } from '@/hooks/useTransactions';
import { ArrowDownLeft, ArrowUpRight, History, Link, Wallet, CreditCard, Receipt } from 'lucide-react';
import { PageOnboardingTooltip, OnboardingSlide } from '@/components/onboarding/PageOnboardingTooltip';

const walletSlides: OnboardingSlide[] = [
  { icon: Wallet, secondaryIcons: [CreditCard], title: 'Your Wallet', description: 'View your balance, linked accounts, and overall financial snapshot in one place. Your funds from deals and payments are managed here.', gradient: 'from-primary/20 to-primary/5' },
  { icon: ArrowDownLeft, secondaryIcons: [ArrowUpRight], title: 'Deposits & Withdrawals', description: 'Deposit funds to your wallet or withdraw earnings to your linked bank account. All transactions are tracked and secure.', gradient: 'from-blue-500/20 to-blue-500/5' },
  { icon: Receipt, secondaryIcons: [History], title: 'Transaction History', description: 'See your recent transactions at a glance, or view your full history for detailed records of every deposit, withdrawal, and payment.', gradient: 'from-emerald-500/20 to-emerald-500/5' },
];
import { useNavigate } from 'react-router-dom';
import { useEnhancedMobile } from '@/hooks/use-enhanced-mobile';

const WalletPage = () => {
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [linkAccountsOpen, setLinkAccountsOpen] = useState(false);
  const { wallet, loading: walletLoading } = useWallet();
  const { transactions, loading: transactionsLoading } = useTransactions({ limit: 10 });
  const navigate = useNavigate();
  const { isMobile } = useEnhancedMobile();

  return (
    <Layout>
      <PageHeader title="Wallet." subtitle="Manage your funds and transactions">
        <Button
          variant="outline"
          onClick={() => setLinkAccountsOpen(true)}
          className="bg-white/10 border-white/20 text-white hover:bg-white/20"
        >
          <Link className="w-4 h-4 mr-2" />
          Link Accounts
        </Button>
      </PageHeader>
      
      <div className={`container mx-auto max-w-[1200px] ${isMobile ? 'px-2 py-2' : 'px-6 py-6'}`}>
        <PageOnboardingTooltip storageKey="wallet_tooltip_dismissed" slides={walletSlides} dialogTitle="Wallet Walkthrough" />
        <WalletHeader wallet={wallet} loading={walletLoading} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Button
            size="lg"
            onClick={() => setDepositOpen(true)}
            className="h-auto py-4 flex flex-col gap-2"
          >
            <ArrowDownLeft className="w-6 h-6" />
            <span>Deposit Funds</span>
          </Button>

          <Button
            size="lg"
            variant="outline"
            onClick={() => setWithdrawOpen(true)}
            className="h-auto py-4 flex flex-col gap-2"
          >
            <ArrowUpRight className="w-6 h-6" />
            <span>Withdraw Funds</span>
          </Button>

          <Button
            size="lg"
            variant="outline"
            onClick={() => navigate('/transaction-history')}
            className="h-auto py-4 flex flex-col gap-2"
          >
            <History className="w-6 h-6" />
            <span>View All History</span>
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Recent Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <TransactionList
              transactions={transactions}
              loading={transactionsLoading}
            />
          </CardContent>
        </Card>

        <DepositModal open={depositOpen} onOpenChange={setDepositOpen} />
        <WithdrawModal open={withdrawOpen} onOpenChange={setWithdrawOpen} />
        <LinkAccountsModal open={linkAccountsOpen} onOpenChange={setLinkAccountsOpen} />
      </div>
    </Layout>
  );
};

export default WalletPage;